import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { RootReducerState } from '../../redux/Reducers';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { CSRPCustomerBillingGroupType, CSRPRouteType } from '../../types/csrp/csrp.type';
import { CreatePRIDto, ImportDIDsFromPRIDto, ImportPRIDto, PRIType, UpdatePRIDto } from '../../types/pri/pri.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { additionalQueryParams } from '../../utils/utilities';

export const getPRIList = createAsyncThunk<
  PaginationType<CSRPCustomerBillingGroupType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('csrp/getPRIList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );
    const mergedParams = { ...params, ...additionalParams };

    const response = await axios.get<PaginationType<CSRPCustomerBillingGroupType>>(
      `${process.env.REACT_APP_API_URL}/pri`,
      controller ? { signal: controller.signal, params: mergedParams } : { params: mergedParams }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PRI!' });
  }
});

export const getBillingGroupsList = createAsyncThunk<
  PaginationType<CSRPCustomerBillingGroupType>,
  Omit<RouteParamsType, 'esi_id'> & { esi_id: number; controller?: AbortController },
  { state: RootReducerState }
>('csrp/getBillingGroupsList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const response = await axios.get<PaginationType<CSRPCustomerBillingGroupType>>(
      `${process.env.REACT_APP_API_URL}/csrp/customer-billing-groups`,
      controller ? { signal: controller.signal, params } : { params }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching billing groups!' });
  }
});

export const getRoutesList = createAsyncThunk<
  PaginationType<CSRPRouteType>,
  Omit<RouteParamsType, 'esi_id'> & {
    billing_group_id: number;
    esi_id: number;
    rate_id?: number;
    controller?: AbortController;
  },
  { state: RootReducerState }
>('csrp/getRoutesList', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const response = await axios.get<PaginationType<CSRPRouteType>>(
      `${process.env.REACT_APP_API_URL}/csrp/routes`,
      controller ? { signal: controller.signal, params } : { params }
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching DIDs!' });
  }
});

export const postImportPRI = createAsyncThunk<{ success: boolean }, ImportPRIDto, { state: RootReducerState }>(
  'csrp/postImportPRI',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post<{ success: boolean }>(
        `${process.env.REACT_APP_API_URL}/pri/import-from-csrp`,
        data
      );

      thunkAPI.dispatch(getPRIList(thunkAPI.getState().csrpReducer.priRouteParams));

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating PRI!' });
    }
  }
);

export const postImportDIDsFormPRI = createAsyncThunk<
  { success: boolean },
  ImportDIDsFromPRIDto,
  { state: RootReducerState }
>('csrp/postImportDIDsFormPRI', async (data, thunkAPI) => {
  try {
    const response = await axios.post<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/dids/import-from-csrp`,
      data
    );

    thunkAPI.dispatch(getPRIDetails({ id: data.pri.id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating DIDs!' });
  }
});

export const postPRIDetails = createAsyncThunk<{ success: boolean }, CreatePRIDto, { state: RootReducerState }>(
  'csrp/postPRIDetails',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/pri`, data);

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating PRI!' });
    }
  }
);

export const patchPRIDetails = createAsyncThunk<
  { success: boolean },
  { id: number; data: UpdatePRIDto },
  { state: RootReducerState }
>('csrp/patchPRIDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/pri/${id}`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating PRI!' });
  }
});

export const deletePRIDetails = createAsyncThunk<
  { success: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('csrp/deletePRIDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/pri/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(getPRIList(thunkAPI.getState().csrpReducer.priRouteParams));
    }

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting PRI!' });
  }
});

export const patchPriDnisPrefix = createAsyncThunk<
  void,
  { id: number; data: { dnisPrefix: string } },
  { state: RootReducerState; rejectValue: string }
>('csrp/patchPriDnisPrefix', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<void>(`${process.env.REACT_APP_API_URL}/pri/${id}/dnis-prefix`, data);
    thunkAPI.dispatch(getPRIDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error });
  }
});

export const getPRIDetails = createAsyncThunk<PRIType, { id: number }, { state: RootReducerState }>(
  'csrp/getPRIDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<PRIType>(`${process.env.REACT_APP_API_URL}/pri/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching PRI!' });
    }
  }
);
